/* eslint-disable no-useless-return */
import React, { useEffect, useState } from 'react';
import TextInput from 'components/input/text';
import FormulaInput from 'components/formula-entry/formula';
import MarkerSearchBox from 'components/formula-entry/marker-search-box';
import MarkerSearchResults from 'components/formula-entry/marker-search-results';
import { IFormulaEntryState, IMarker, IMarkerList } from 'store/formula-entry/types';
import { FormulasInterface, IDeal } from 'store/deals/types';
import { ModalContainerDark, OptionModal } from 'components/modal-provider/modal';
import {
  ButtonContainer,
  CancelButton,
  CloseIcon,
  Container,
  ContentSection,
  ContentSectionPanel,
  ContentSeparatorContractTerm,
  ContentWithScroll,
  DoubleArrowIcon,
  FormControlLabel,
  InformationPanel,
  InputError,
  InputGroup,
  Label,
  OptionSectionPanel,
  SaveFormulaButton,
  Title,
  TitleContainer,
  Radio,
  EditLabel,
  RadioGroup,
  VolumeOptionModalSpacing,
} from 'components/formula-entry/modals/_common';
import {
  EOptionOwner,
  IVolumeOption,
  ECycle,
  EContractTermOptionType,
  IDealProduct,
  IPrice,
} from 'store/field-options/types';
import { handleError } from 'lib/helpers/error-handler';
import messages from 'constants/messages';
import { EInputField, getOptionInputError } from 'lib/validators/option';
import { isMatch } from 'store/formula-entry/match';
import {
  validateFormula,
  getUnitLabel,
  triggerDuplicateMarkerPreventionTimer,
} from 'components/formula-entry/modals/helpers';
import { capitalizeFirstLetter } from 'lib/helpers/string-manipulators';
import optionErrors from 'lib/validators/option.errors';
import ToggleButton from 'components/buttons/toggle';
import { ELoadingType } from 'store/loading/types';
import MarkerFilters from '../formula-list/marker-filters';
import { TConcatArraysReturnType } from '../types';

interface IFormulaEntryContainerProps {
  cancelFormulaEdit: () => void;
  dealEdit: IDeal;
  formulaEntry: IFormulaEntryState;
  getDealEvaluation: () => void;
  setLoading: (loading: ELoadingType) => void;
  updateProductField: Function;
  allTypesMarkerList: TConcatArraysReturnType[];
  updateMarkerList: Function;
}

const volumeOptionInitialState: IVolumeOption = {
  enabled: true,
  owner: EOptionOwner.SHELL,
  volume: null,
  count: 12,
  cycle: ECycle.CONTRACT,
  notice: 90,
  basePrice: '',
  shellBatna: '',
  custBatna: '',
  description: '',
};

interface IContractTermError {
  volume: string | null;
  count: string | null;
  notice: string | null;
  basePrice: string | null;
  shellBatna: string | null;
  custBatna: string | null;
}

const errorsInitialState: IContractTermError = {
  volume: '',
  count: '',
  notice: '',
  basePrice: '',
  shellBatna: '',
  custBatna: '',
};

enum EFormula {
  NONE,
  BASE_PRICE,
  SHELL_BATNA,
  CUSTOMER_BATNA,
}

// Handle change to contractOptionType, mark basePrice as readOnly, and
// change to product's base price IF earlyTerm is selected
function handleContractOptionTypeChange(
  c: EContractTermOptionType,
  setContractOptionType: {
    (value: React.SetStateAction<EContractTermOptionType>): void;
    (value: React.SetStateAction<EContractTermOptionType>): void;
    (arg0: EContractTermOptionType): void;
  },
  productIndex: string | number | null,
  products: IDealProduct[] | undefined,
  setVolumeOption: Function,
  volumeOption: IVolumeOption
) {
  setContractOptionType(c);
  if (!products || productIndex === null) {
    return;
  }
  const { basePrice } = products[productIndex];
  setVolumeOption({
    ...volumeOption,
    basePrice: basePrice ? basePrice.formula : null,
  });
}

function processInsertMarkers(
  name: string,
  timer: number | null,
  setTimer: Function,
  activeFormula: EFormula,
  volumeOption: IVolumeOption,
  cursorPosition: number,
  setVolumeOption: Function
) {
  // Check for active timer, if found, prevent insertion of duplicate marker
  if (timer) {
    return;
  }

  // Kickoff new timer to handle double-clicking of markers
  triggerDuplicateMarkerPreventionTimer(setTimer);

  // Determine which formula is being edited
  if (activeFormula === EFormula.BASE_PRICE) {
    const formula = volumeOption.basePrice ?? '';
    const formulaValueUpdated = [formula.slice(0, cursorPosition), `[${name}]`, formula.slice(cursorPosition)].join('');
    setVolumeOption({
      ...volumeOption,
      basePrice: formulaValueUpdated,
    });
  } else if (activeFormula === EFormula.SHELL_BATNA) {
    const formula = volumeOption.shellBatna ?? '';
    const formulaValueUpdated = [formula.slice(0, cursorPosition), `[${name}]`, formula.slice(cursorPosition)].join('');
    setVolumeOption({
      ...volumeOption,
      shellBatna: formulaValueUpdated,
    });
  } else if (activeFormula === EFormula.CUSTOMER_BATNA) {
    const formula = volumeOption.custBatna ?? '';
    const formulaValueUpdated = [formula.slice(0, cursorPosition), `[${name}]`, formula.slice(cursorPosition)].join('');
    setVolumeOption({
      ...volumeOption,
      custBatna: formulaValueUpdated,
    });
  }
}

function handleOwnerToggle(
  e: React.ChangeEvent<HTMLInputElement>,
  setVolumeOption: Function,
  volumeOption: IVolumeOption
) {
  setVolumeOption({
    ...volumeOption,
    owner: e.target.value,
  });
}
function EditText() {
  return <EditLabel>Default value BASE & BATNA has been changed</EditLabel>;
}

// Update marker value and filtered list
function handleSearchValueChange(
  e: React.ChangeEvent<HTMLInputElement>,
  availableMarkers: IMarker[] | null,
  setMarkerSearchValue: { (value: React.SetStateAction<string>): void; (arg0: string): void }
) {
  if (!availableMarkers) {
    handleError(messages.errors.noMarkers);
    return;
  }
  const val = e.target.value;
  setMarkerSearchValue(val);
}

// Handle clear button for marker search input
function handleSearchValueClear(
  setMarkerSearchValue: { (value: React.SetStateAction<string>): void; (arg0: string): void },
  setFilteredMarkers: Function,
  availableMarkers: IMarker[] | null
) {
  setMarkerSearchValue('');
  setFilteredMarkers(availableMarkers);
}

const checkPullOptionProductIndex = (productIndex: number | null) => {
  return productIndex === null;
};

const CheckProductIndexValue = (productIndex: number | null) => {
  if (checkPullOptionProductIndex(productIndex)) {
    handleError(messages.errors.default);
  }
};

const getContractTermOptionTypeToClear = (contractOptionType: EContractTermOptionType) => {
  return contractOptionType === EContractTermOptionType.EXTENSION
    ? EContractTermOptionType.EARLY_TERMINATION
    : EContractTermOptionType.EXTENSION;
};

const trimVolumeOptionShellBatna = (volumeOption: IVolumeOption) => {
  return volumeOption.shellBatna ? volumeOption.shellBatna.trim() : '';
};

const trimVolumeOptionCustBatna = (volumeOption: IVolumeOption) => {
  return volumeOption.custBatna ? volumeOption.custBatna.trim() : '';
};

const trimVolumeOptionBasePrice = (volumeOption: IVolumeOption) => {
  return volumeOption.basePrice ? volumeOption.basePrice.trim() : '';
};

const checkVolumeValueEmpty = (volumeOption: { volume: string }) => {
  return volumeOption.volume === '';
};

const validateVolumeOptionBasePriceShellAndCustBatnaValue = async (
  newErrors: IContractTermError,
  volumeOption: IVolumeOption,
  dealFormulas: FormulasInterface[] | undefined
) => {
  const tempNewError = newErrors;
  // Base price
  if (volumeOption.basePrice) {
    const { basePrice: formula } = volumeOption;
    const basePriceErrorResponse = await validateFormula(dealFormulas, [{ formula, name: '' }]);
    const basePriceError = basePriceErrorResponse.data.error;
    if (basePriceError) {
      tempNewError.basePrice = capitalizeFirstLetter(basePriceError.message);
    }
  } else {
    tempNewError.basePrice = optionErrors.requiredField;
  }

  // Shell BATNA
  if (volumeOption.shellBatna) {
    const { shellBatna: formula } = volumeOption;
    const shellBatnaErrorResponse = await validateFormula(dealFormulas, [{ formula, name: '' }]);
    const shellBatnaError = shellBatnaErrorResponse.data.error;
    if (shellBatnaError) {
      tempNewError.shellBatna = capitalizeFirstLetter(shellBatnaError.message);
    }
  } else {
    tempNewError.shellBatna = optionErrors.requiredField;
  }

  // Customer BATNA
  if (volumeOption.owner === EOptionOwner.CUSTOMER && volumeOption.custBatna) {
    const { custBatna: formula } = volumeOption;
    const custBatnaErrorResponse = await validateFormula(dealFormulas, [{ formula, name: '' }]);
    const custBatnaError = custBatnaErrorResponse.data.error;
    if (custBatnaError) {
      tempNewError.custBatna = capitalizeFirstLetter(custBatnaError.message);
    }
  } else if (volumeOption.owner === EOptionOwner.CUSTOMER && !volumeOption.custBatna) {
    tempNewError.custBatna = optionErrors.requiredField;
  }
  return tempNewError;
};

const checkVolumeOptionCount = (volumeOption: { count: number }) => {
  return volumeOption && volumeOption.count < 0;
};

const checkNewErrorVolumeAndCount = (newErrors: IContractTermError) => {
  return !newErrors.volume && !newErrors.count;
};

const checkNewErrorShellAndCustBatna = (newErrors: IContractTermError) => {
  return !newErrors.shellBatna && !newErrors.custBatna;
};

const checkNewErrorNoticeAndBasePrice = (newErrors: IContractTermError) => {
  return !newErrors.notice && !newErrors.basePrice;
};

const checkVolumeCountNoticeAndPriceAndBatnaValidation = (
  volumeCount: boolean,
  noticeAndPrice: boolean,
  batnaValidation: boolean
) => {
  return volumeCount && noticeAndPrice && batnaValidation;
};

const checkContractOptionType = (contractOptionType: EContractTermOptionType) => {
  return contractOptionType === EContractTermOptionType.EARLY_TERMINATION;
};

async function processSave(
  productIndex: number | null,
  contractOptionType: EContractTermOptionType,
  volumeOption: IVolumeOption,
  reqObject: {
    updateProductField: Function;
    getDealEvaluation: { (): void; (): void };
    cancelFormulaEdit: { (): void; (): void };
    setErrors: Function;
  },
  setLoading: { (loading: ELoadingType): void; (arg0: ELoadingType): void },
  dealFormulas: FormulasInterface[] | undefined,
  dealEdit: IDeal
) {
  const volumeOptionReassin = { ...volumeOption };
  CheckProductIndexValue(productIndex);

  // Remove the option inverse (e.g., remove extension if earlyTerm is selected)
  const contractTermOptionTypeToClear = getContractTermOptionTypeToClear(contractOptionType);
  reqObject.updateProductField(productIndex, contractTermOptionTypeToClear, null);
  // Trim input values
  volumeOptionReassin.basePrice = trimVolumeOptionBasePrice(volumeOption);
  volumeOptionReassin.shellBatna = trimVolumeOptionShellBatna(volumeOption);
  volumeOptionReassin.custBatna = trimVolumeOptionCustBatna(volumeOption);

  // Check for empty volume -- delete option scenario
  // @ts-ignore
  if (checkVolumeValueEmpty(volumeOption)) {
    reqObject.updateProductField(productIndex, EContractTermOptionType.EXTENSION, null);
    reqObject.updateProductField(productIndex, EContractTermOptionType.EARLY_TERMINATION, null);
    reqObject.getDealEvaluation();
    reqObject.cancelFormulaEdit();
    return;
  }

  // API calls
  setLoading(ELoadingType.SPINNER);

  // Initialize new error object
  let newErrors = { ...errorsInitialState };

  try {
    newErrors = await validateVolumeOptionBasePriceShellAndCustBatnaValue(newErrors, volumeOption, dealFormulas);
  } catch (e) {
    handleError(e);
    setLoading(ELoadingType.NONE);
  }

  // Parse volume
  volumeOptionReassin.volume = volumeOption.volume;

  // Validate volume
  newErrors.volume = getOptionInputError(EInputField.VOLUME, volumeOption.volume);

  // Validate option count
  // @ts-ignore
  if (checkVolumeOptionCount(volumeOption)) {
    newErrors.count = optionErrors.invalidVolume;
  }

  // Validate notification period
  newErrors.notice = getOptionInputError(EInputField.NOTICE, volumeOption.notice);
  const volumeCount = checkNewErrorVolumeAndCount(newErrors);
  const noticeAndPrice = checkNewErrorNoticeAndBasePrice(newErrors);
  const batnaValidation = checkNewErrorShellAndCustBatna(newErrors);

  // Update deal, re-evaluate, and close modal if no errors exist
  if (checkVolumeCountNoticeAndPriceAndBatnaValidation(volumeCount, noticeAndPrice, batnaValidation)) {
    // Mark basePrice as null for earlyTerm scenario (only being used for display purposes)
    if (checkContractOptionType(contractOptionType)) {
      volumeOptionReassin.basePrice = null;
    }

    reqObject.updateProductField(productIndex, contractOptionType, volumeOption);
    reqObject.getDealEvaluation();
    reqObject.cancelFormulaEdit();
  } else {
    reqObject.setErrors(newErrors);
    setLoading(ELoadingType.NONE);
  }
}

// Build dynamic label based on selected option type
function buildTimeToLabel(contractOptionType: EContractTermOptionType) {
  const word = contractOptionType === EContractTermOptionType.EXTENSION ? 'EXTEND' : 'EARLY TERMINATE';
  return `TIME TO ${word} (MONTHS)`;
}

const getVolumeUnitLabel = (products: IDealProduct[] | undefined, productIndex: number | null) => {
  let volumeUnitLabel = '';
  if (products && productIndex !== null) {
    volumeUnitLabel = `(${products[productIndex].volumeUnit})`;
  }
  return volumeUnitLabel;
};

const getMarkers = (markerList: IMarkerList[], allTypesMarkerList: TConcatArraysReturnType[]) => {
  return markerList || allTypesMarkerList;
};

const returnHandleError = (availableMarkers: IMarker[] | null) => {
  if (!availableMarkers) {
    handleError(messages.errors.noMarkers);
  }
};

const checkIsInitializedAndProductsValue = (
  isInitialized: boolean,
  products: IDealProduct[] | undefined,
  productIndex: number | null
) => {
  return !isInitialized && products && productIndex !== null;
};

const getMainBatnaFormula = (mainBatna: IPrice | undefined) => {
  return mainBatna ? mainBatna.formula : '';
};

const getBasePriceFormula = (basePrice: IPrice | undefined) => {
  return basePrice ? basePrice.formula : '';
};

const getProductsDetailsForPushValue = (
  volumeOption: IVolumeOption,
  products: IDealProduct[] | undefined,
  productIndex: number | null,
  setContractOptionType: Function,
  setVolumeOption: Function,
  setMainbatnaCopy: Function,
  setBasePriceCopy: Function
) => {
  if (products && productIndex !== null) {
    const { mainBatna, basePrice, earlyTerm, extension } = products[productIndex];
    let option;
    if (earlyTerm) {
      option = earlyTerm;
      setContractOptionType(EContractTermOptionType.EARLY_TERMINATION);
    } else if (extension) {
      option = extension;
    }

    if (option) {
      setVolumeOption(option);
    } else {
      const { volume } = products[productIndex];
      setVolumeOption({
        ...volumeOption,
        basePrice: getBasePriceFormula(basePrice),
        shellBatna: getMainBatnaFormula(mainBatna),
        custBatna: getMainBatnaFormula(mainBatna),
        volume,
      });
    }
    if (mainBatna) {
      setMainbatnaCopy(mainBatna.formula);
    }
    if (basePrice) {
      setBasePriceCopy(basePrice.formula);
    }
  }
};

const checkContractOptionTypeExtension = (contractOptionType: EContractTermOptionType) => {
  return contractOptionType === EContractTermOptionType.EXTENSION;
};

const getVolumeOptionVolume = (volumeOption: IVolumeOption) => {
  return volumeOption.volume ?? '';
};

const getVolumeOptionCount = (volumeOption: IVolumeOption) => {
  return volumeOption.count ?? '';
};

const checkVolumeValueAndUndefined = (e: React.ChangeEvent<HTMLInputElement>) => {
  return parseFloat(e.target.value) || undefined;
};

const getVolumeOptionNotice = (volumeOption: IVolumeOption) => {
  return volumeOption.notice ?? '';
};

const getVolumeOptionDescription = (volumeOption: IVolumeOption) => {
  return volumeOption.description ?? '';
};

const getVolumeOptionBasePrice = (volumeOption: IVolumeOption) => {
  return volumeOption.basePrice ?? '';
};

const checkActiveFormulaForBasePrice = (activeFormula: EFormula) => {
  return activeFormula === EFormula.BASE_PRICE;
};

const checkVolumeOptionBasePriceAndBasePriceCopy = (volumeOption: IVolumeOption, basePriceCopy: string) => {
  return (
    (volumeOption.basePrice !== basePriceCopy || (volumeOption.basePrice === '' && basePriceCopy !== '')) && (
      <EditText />
    )
  );
};

const getVolumeOptionShellBatna = (volumeOption: IVolumeOption) => {
  return volumeOption.shellBatna ?? '';
};

const checkActiveFormulaForShellBatna = (activeFormula: EFormula) => {
  return activeFormula === EFormula.SHELL_BATNA;
};

const checkVolumeOptionShellBatnaMainBatnaCopy = (volumeOption: IVolumeOption, mainBatnaCopy: string) => {
  return (
    (volumeOption.shellBatna !== mainBatnaCopy || (volumeOption.shellBatna === '' && mainBatnaCopy !== '')) && (
      <EditText />
    )
  );
};

const checkVolumeOptionOwner = (volumeOption: IVolumeOption) => {
  return volumeOption.owner === EOptionOwner.CUSTOMER;
};

const getVolumeOptionCustBatna = (volumeOption: IVolumeOption) => {
  return volumeOption.custBatna ?? '';
};

const checkActiveFormulaForCustomerBatna = (activeFormula: EFormula) => {
  return activeFormula === EFormula.CUSTOMER_BATNA;
};

export default ({
  cancelFormulaEdit,
  dealEdit,
  formulaEntry,
  getDealEvaluation,
  setLoading,
  updateProductField,
  allTypesMarkerList,
  updateMarkerList,
}: IFormulaEntryContainerProps) => {
  const { availableMarkers, productIndex, markerList } = formulaEntry;
  const { products, formulas: dealFormulas } = dealEdit;

  const [isInitialized, setIsInitialized] = useState(false);
  const [contractOptionType, setContractOptionType] = useState(EContractTermOptionType.EXTENSION);
  const [volumeOption, setVolumeOption] = useState(volumeOptionInitialState);
  const [errors, setErrors] = useState(errorsInitialState);
  const [shellBatnaEdit, setShellBatnaEdit] = useState(false);
  const [basePriceEdit, setBasePriceEdit] = useState(false);
  const [mainBatnaCopy, setMainbatnaCopy] = useState('');
  const [basePriceCopy, setBasePriceCopy] = useState('');
  const [activeFormula, setActiveFormula] = useState(EFormula.SHELL_BATNA);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [filteredMarkers, setFilteredMarkers] = useState(availableMarkers);
  const [markerSearchValue, setMarkerSearchValue] = useState('');
  const [timer, setTimer] = useState(null);

  useEffect(() => {
    setFilteredMarkers(markerList);
  }, [markerList]);

  // Build dynamic labels
  const pricePerVolumeUnitLabel = getUnitLabel(products, productIndex);
  const volumeUnitLabel = getVolumeUnitLabel(products, productIndex);

  // Inject marker at cursorPosition
  function insertMarker(name: string) {
    processInsertMarkers(name, timer, setTimer, activeFormula, volumeOption, cursorPosition, setVolumeOption);
  }

  // Manage filtered marker list
  useEffect(() => {
    returnHandleError(availableMarkers);
    const markers = getMarkers(markerList, allTypesMarkerList);
    const filteredMarkersNew = markers.filter((marker: IMarker) => isMatch(markerSearchValue, marker.name));
    setFilteredMarkers(filteredMarkersNew);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableMarkers, markerList, markerSearchValue]);

  useEffect(() => {
    if (checkIsInitializedAndProductsValue(isInitialized, products, productIndex)) {
      setIsInitialized(true);
      getProductsDetailsForPushValue(
        volumeOption,
        products,
        productIndex,
        setContractOptionType,
        setVolumeOption,
        setMainbatnaCopy,
        setBasePriceCopy
      );
    }
  }, [volumeOption, isInitialized, productIndex, products]);

  async function save() {
    const reqObj = {
      getDealEvaluation,
      cancelFormulaEdit,
      setErrors,
      updateProductField,
    };
    processSave(productIndex, contractOptionType, volumeOption, reqObj, setLoading, dealFormulas, dealEdit);
  }
  return (
    <ModalContainerDark>
      <OptionModal>
        <Container>
          <TitleContainer>
            <Title>Contract Term Option Entry</Title>
            <CloseIcon onClick={cancelFormulaEdit} />
          </TitleContainer>

          <ContentWithScroll>
            <VolumeOptionModalSpacing />
            <ContentSection>
              <OptionSectionPanel>
                <InputGroup>
                  <Label>CONTRACT TERM</Label>
                  <ToggleButton
                    isPrimaryOptionActive={checkContractOptionTypeExtension(contractOptionType)}
                    primaryOptionText="Extension"
                    secondaryOptionText="Early Termination"
                    onPrimaryOptionClick={() =>
                      handleContractOptionTypeChange(
                        EContractTermOptionType.EXTENSION,
                        setContractOptionType,
                        productIndex,
                        products,
                        setVolumeOption,
                        volumeOption
                      )
                    }
                    onSecondaryOptionClick={() =>
                      handleContractOptionTypeChange(
                        EContractTermOptionType.EARLY_TERMINATION,
                        setContractOptionType,
                        productIndex,
                        products,
                        setVolumeOption,
                        volumeOption
                      )
                    }
                  />
                  <InputError />
                </InputGroup>
                <InputGroup>
                  <Label>OWNER</Label>
                  <RadioGroup
                    data-testid="tid-owner-toggle"
                    aria-label="view-type"
                    name="radio-view-type"
                    value={volumeOption.owner}
                    onChange={e => handleOwnerToggle(e, setVolumeOption, volumeOption)}
                  >
                    <FormControlLabel value={EOptionOwner.SHELL} control={<Radio />} label="Shell" />
                    <FormControlLabel value={EOptionOwner.CUSTOMER} control={<Radio />} label="Customer" />
                  </RadioGroup>
                  <InputError />
                </InputGroup>

                <InputGroup>
                  <Label
                    data-valid={!!errors.volume}
                    htmlFor="monthly-option-volume"
                  >{`MONTHLY OPTION VOLUME ${volumeUnitLabel.toUpperCase()}`}</Label>
                  <TextInput
                    data-testid="tid-volume-option-volume"
                    value={getVolumeOptionVolume(volumeOption)}
                    onChange={e => setVolumeOption({ ...volumeOption, volume: parseFloat(e.target.value) })}
                    type="number"
                    id="monthly-option-volume"
                  />
                  <InputError>{errors.volume}</InputError>
                </InputGroup>

                <InputGroup>
                  <Label data-valid={!!errors.count}>{buildTimeToLabel(contractOptionType)}</Label>
                  <TextInput
                    data-testid="tid-volume-option-count"
                    value={getVolumeOptionCount(volumeOption)}
                    onChange={e => setVolumeOption({ ...volumeOption, count: checkVolumeValueAndUndefined(e) })}
                    type="number"
                  />
                  <InputError>{errors.count}</InputError>
                </InputGroup>

                <InputGroup>
                  <Label data-valid={!!errors.notice}>NOTIFICATION PERIOD (DAYS)</Label>
                  <TextInput
                    data-testid="tid-notification-period"
                    value={getVolumeOptionNotice(volumeOption)}
                    onChange={e => setVolumeOption({ ...volumeOption, notice: parseFloat(e.target.value) })}
                    type="number"
                  />
                  <InputError>{errors.notice}</InputError>
                </InputGroup>
              </OptionSectionPanel>
              <InformationPanel>
                <InputGroup>
                  <Label>OPTION DISPLAY LABEL</Label>
                  <TextInput
                    data-testid="tid-option-display-label"
                    value={getVolumeOptionDescription(volumeOption)}
                    onChange={e => setVolumeOption({ ...volumeOption, description: e.target.value })}
                    type="string"
                  />
                  <InputError />
                </InputGroup>
              </InformationPanel>
            </ContentSection>

            <ContentSeparatorContractTerm>
              <div>
                <DoubleArrowIcon />
                <span>Scroll down to update price formulas</span>
              </div>
            </ContentSeparatorContractTerm>

            <ContentSection>
              <ContentSectionPanel>
                <InputGroup>
                  <Label
                    data-valid={!!errors.basePrice}
                  >{`BASE FORMULA ${pricePerVolumeUnitLabel.toUpperCase()}`}</Label>
                  <FormulaInput
                    formulaInputValue={getVolumeOptionBasePrice(volumeOption)}
                    isActive={checkActiveFormulaForBasePrice(activeFormula)}
                    onChange={e => {
                      setVolumeOption({ ...volumeOption, basePrice: e.target.value });
                      if (!basePriceEdit) {
                        setBasePriceEdit(true);
                      }
                    }}
                    readOnly={checkContractOptionType(contractOptionType)}
                    setActiveFormula={() => setActiveFormula(EFormula.BASE_PRICE)}
                    setCursorPosition={(p: number) => setCursorPosition(p)}
                    setFormula={(value: string) => setVolumeOption({ ...volumeOption, basePrice: value })}
                  />
                  {checkVolumeOptionBasePriceAndBasePriceCopy(volumeOption, basePriceCopy)}
                  <InputError>{errors.basePrice}</InputError>
                </InputGroup>

                <InputGroup>
                  <Label
                    data-valid={!!errors.shellBatna}
                  >{`SHELL BATNA FORMULA ${pricePerVolumeUnitLabel.toUpperCase()}`}</Label>
                  <FormulaInput
                    formulaInputValue={getVolumeOptionShellBatna(volumeOption)}
                    isActive={checkActiveFormulaForShellBatna(activeFormula)}
                    onChange={e => {
                      setVolumeOption({ ...volumeOption, shellBatna: e.target.value });
                      if (!shellBatnaEdit) {
                        setShellBatnaEdit(true);
                      }
                    }}
                    setActiveFormula={() => setActiveFormula(EFormula.SHELL_BATNA)}
                    setCursorPosition={(p: number) => setCursorPosition(p)}
                    setFormula={(value: string) => setVolumeOption({ ...volumeOption, shellBatna: value })}
                  />
                  {checkVolumeOptionShellBatnaMainBatnaCopy(volumeOption, mainBatnaCopy)}
                  <InputError>{errors.shellBatna}</InputError>
                </InputGroup>

                {checkVolumeOptionOwner(volumeOption) ? (
                  <InputGroup>
                    <Label data-valid={!!errors.custBatna}>{`CUSTOMER BATNA FORMULA ${getUnitLabel(
                      products,
                      productIndex
                    ).toUpperCase()}`}</Label>
                    <FormulaInput
                      formulaInputValue={getVolumeOptionCustBatna(volumeOption)}
                      isActive={checkActiveFormulaForCustomerBatna(activeFormula)}
                      onChange={e => setVolumeOption({ ...volumeOption, custBatna: e.target.value })}
                      setActiveFormula={() => setActiveFormula(EFormula.CUSTOMER_BATNA)}
                      setCursorPosition={(p: number) => setCursorPosition(p)}
                      setFormula={(value: string) => setVolumeOption({ ...volumeOption, custBatna: value })}
                    />
                    <InputError>{errors.custBatna}</InputError>
                  </InputGroup>
                ) : null}
              </ContentSectionPanel>

              <ContentSectionPanel>
                <Label>SEARCH FOR MARKER</Label>
                <MarkerSearchBox
                  value={markerSearchValue}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleSearchValueChange(e, availableMarkers, setMarkerSearchValue)
                  }
                  onClear={() => handleSearchValueClear(setMarkerSearchValue, setFilteredMarkers, availableMarkers)}
                />
                <MarkerFilters allTypesMarkerList={allTypesMarkerList} updateMarkerList={updateMarkerList} />
                <MarkerSearchResults markers={filteredMarkers} onClick={insertMarker} />
              </ContentSectionPanel>
            </ContentSection>
          </ContentWithScroll>

          <ButtonContainer>
            <CancelButton onClick={cancelFormulaEdit}>Cancel</CancelButton>
            <SaveFormulaButton onClick={save}>Apply Option</SaveFormulaButton>
          </ButtonContainer>
        </Container>
      </OptionModal>
    </ModalContainerDark>
  );
};
